import React from 'react'
import Smartview from 'widgets/smartviews/Smartview'
import { Translation, Translator } from '@morningtrain/react-translations'
import * as Displays from 'support/displays'
import SmartviewContentSection from 'widgets/smartviews/partials/SmartviewContentSection'
import SmartviewTextGroup from 'widgets/smartviews/partials/SmartviewTextGroup'
import SmartviewText from 'widgets/smartviews/partials/SmartviewText'
import { WhenModelValue } from '@morningtrain/react-resources'
import AvatarWrapper from 'ui/partials/AvatarWrapper'
import SmartviewContentSeparator from 'widgets/smartviews/partials/SmartviewContentSeparator'
import EmploymentIdsEdit from 'widgets/smartviews/hr/salary/partials/EmploymentIdsEdit'
import MountSmartviewWithModelOnClick from 'support/utilities/MountSmartviewWithModelOnClick'
import UserShiftsSmartview from 'widgets/smartviews/hr/salary/UserShiftsSmartview'
import * as UI from 'ui'

export default function UserSalaryOverviewSmartview () {
  return (
    <Translator namespace='smartviews.user_salary_overview'>
      <Smartview>

        <SmartviewContentSection>
          <div className='flex items-center'>
            <AvatarWrapper large>
              <Displays.Image name='user.avatar_url' />
            </AvatarWrapper>
            <div className='mr-6'>
              <div className='font-medium text-base leading-tight'>
                <Displays.Text name='user.name' />
              </div>
              <div className='text-sm text-gray-500 leading-tight'>
                <Displays.Text name='primary_department_name' />
              </div>
              <div className='text-sm text-gray-500 leading-tight'>
                <Displays.Text name='employment_name' />
              </div>
            </div>
          </div>
        </SmartviewContentSection>

        <SmartviewContentSeparator />

        <SmartviewContentSection>
          <EmploymentIdsEdit />
        </SmartviewContentSection>

        <SmartviewContentSeparator />

        <SmartviewContentSection>

          <SmartviewTextGroup>
            <SmartviewText
              label={(
                <>
                  <span><Translation name='hours_worked' /></span>
                  <Displays.Duration name='hours_worked' />
                </>
              )}
              textSize='text-xs'
            >
              <div className='flex justify-between'>
                <span><Translation name='hours_admin_worked' /></span>
                <Displays.Duration name='hours_admin_worked' />
              </div>
              <div className='flex justify-between'>
                <span><Translation name='in_primary_department' /></span>
                <Displays.Duration name='hours_worked_in_primary_department' />
              </div>
              <div className='flex justify-between'>
                <span><Translation name='in_this_department' /></span>
                <Displays.Duration name='hours_worked_in_this_department' />
              </div>
              <div className='flex justify-between'>
                <span><Translation name='in_other_departments' /></span>
                <Displays.Duration name='hours_worked_in_other_departments' />
              </div>
            </SmartviewText>
            <MountSmartviewWithModelOnClick smartview={<UserShiftsSmartview />} replace={false} keepTreeOnPush>
              <UI.Button>
                <Translation name='actions.view_shifts' />
              </UI.Button>
            </MountSmartviewWithModelOnClick>
            <SmartviewText
              label={(
                <>
                  <span><Translation name='shifts_count' /></span>
                  <Displays.Integer
                    name='shifts_count'
                  />
                </>
              )}
              textSize='text-xs'
            >
              <div className='flex justify-between'>
                <span><Translation name='in_primary_department' /></span>
                <Displays.Integer name='shifts_count_in_primary_department' />
              </div>
              <div className='flex justify-between'>
                <span><Translation name='in_this_department' /></span>
                <Displays.Integer name='shifts_count_in_this_department' />
              </div>
              <div className='flex justify-between'>
                <span><Translation name='in_other_departments' /></span>
                <Displays.Integer name='shifts_count_in_other_departments' />
              </div>
              <div className='flex justify-between'>
                <span><Translation name='expected_employee_food_cost' /></span>
                <Displays.Currency name='expected_employee_food_cost' />
              </div>
            </SmartviewText>
            <SmartviewText
              label={(
                <>
                  <span><Translation name='planned_shifts' /></span>
                  <Displays.Duration
                    name='planned_shifts'
                  />
                </>
              )} textSize='text-xs'
            >
              <div className='flex justify-between'>
                <span><Translation name='in_primary_department' /></span>
                <Displays.Duration name='planned_shifts_in_primary_department' />
              </div>
              <div className='flex justify-between'>
                <span><Translation name='in_this_department' /></span>
                <Displays.Duration name='planned_shifts_in_this_department' />
              </div>
              <div className='flex justify-between'>
                <span><Translation name='in_other_departments' /></span>
                <Displays.Duration name='planned_shifts_in_other_departments' />
              </div>
            </SmartviewText>
            <SmartviewText
              label={(
                <>
                  <span><Translation name='salary_amount' /></span>
                  <Displays.Currency
                    name='amount'
                  />
                </>
              )} textSize='text-xs'
            >
              <WhenModelValue name='salary_type' condition='hourly'>
                <div className='flex justify-between'>
                  <span><Translation name='hourly_wage' /></span>
                  <Displays.Currency name='wage' />
                </div>
              </WhenModelValue>
            </SmartviewText>
            <SmartviewText label={<Translation name='personal_information' />}>
              <div className='flex justify-between'>
                <span><Translation name='address' /></span>
                <Displays.Text name='user.address' />
              </div>
              <div className='flex justify-between'>
                <span><Translation name='cpr' /></span>
                <Displays.Text name='user.cpr' />
              </div>
            </SmartviewText>
            <SmartviewText label={<Translation name='account_information' />}>
              <div className='flex justify-between'>
                <span><Translation name='registration' /></span>
                <Displays.Text name='user.bank_reg' />
              </div>
              <div className='flex justify-between'>
                <span><Translation name='account_number' /></span>
                <Displays.Text name='user.bank_account' />
              </div>
            </SmartviewText>
            <SmartviewText label={<Translation name='employment_information' />}>
              <div className='flex justify-between'>
                <span><Translation name='hired_at' /></span>
                <Displays.Date name='user.hired_at' />
              </div>
              <div className='flex justify-between'>
                <span><Translation name='tax_type' /></span>
                <Displays.Enum enum='tax_type' name='user.tax_type' />
              </div>
              <div className='flex justify-between'>
                <span><Translation name='work_type' /></span>
                <Displays.Enum enum='work_type' name='user.work_type' />
              </div>
            </SmartviewText>
          </SmartviewTextGroup>

        </SmartviewContentSection>

      </Smartview>
    </Translator>
  )
}
